import React, {FC, Fragment, useEffect, useMemo, useRef, useState} from "react";
import {Badge, Button, Col, Container, FormSelect, InputGroup, Row} from "react-bootstrap";
import {utils, writeFile} from 'xlsx';
import {
    dateToString,
    dateWithMaxTime,
    dateWithZeroTime,
    getDatesInPeriod, hoursAsShortString,
    showErrorsListInToast, zeroToEmptyStringAsCurrency
} from "../../../../tools";
import {MaybeDate} from "../../../../types/Maybe";
import {useAppSelector} from "../../../../hooks";
import {RRLogisticType} from "../../RRLogisticType";
import ReactDatePicker from "react-datepicker";
import {collection, getDocs, query, QueryConstraint, where} from "firebase/firestore";
import {appFirestore} from "../../../../../index";
import {selectCurrentEmployee} from "../../../../../features/AppStateSlice";
import {useCollectionData} from "react-firebase-hooks/firestore";
import {logisticsFBConverter} from "../../LogisticsFBConverter";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCalendar, faFileExcel} from "@fortawesome/free-solid-svg-icons";
import {employeesFBConverter} from "../../../employees/EmployeesFBConverter";
import {stringAsDepartment} from "../../../../types/RRDepartmentType";
import {calculateLogisticsReportHours} from "./LogisticsTimeReportComputing";
import {
    isResolvedPeriodStartInRange,
    mergeLogisticsById,
    resolveLogisticPeriod,
} from "../transport-work-report/LogisticsWorkReportComputing";

type ReportMonthOption = {
    label: string;
    value: string;
    periodFrom: Date;
    periodTo: Date;
}

const BG_MONTH_NAMES = [
    "януари",
    "февруари",
    "март",
    "април",
    "май",
    "юни",
    "юли",
    "август",
    "септември",
    "октомври",
    "ноември",
    "декември",
];

function createMonthOption(baseDate: Date): ReportMonthOption {
    const periodFrom = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
    const periodTo = new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 0);

    return {
        label: `${BG_MONTH_NAMES[periodFrom.getMonth()]}, ${periodFrom.getFullYear()}`,
        value: `${periodFrom.getFullYear()}-${String(periodFrom.getMonth() + 1).padStart(2, "0")}`,
        periodFrom,
        periodTo,
    };
}

function getMonthOptions(today: Date): ReportMonthOption[] {
    return Array.from({length: 13}, (_, index) => {
        const optionDate = new Date(today.getFullYear(), today.getMonth() - index, 1);
        return createMonthOption(optionDate);
    });
}

type RRReportType = {
    date: Date;
    driverId: string;
    driverName: string;
    regularHours: number;
    overtimeHours: number;
    regRate: number;
    overRate: number;
    regPrice: number;
    overPrice: number;
    distanceKm: number;
    courses: RRLogisticType[];
    minHour: number;
    maxHour: number;
    // true when the employee is on paid holiday/leave for that day (e.g. DAYTYPE == HOLIDAY)
    isEmployeeHoliday: boolean;
    // true when the day is an official bank holiday (or weekend treated as bankHoliday for overtime purposes)
    isBankHoliday: boolean;
}

const LogisticsTimeReportView: FC = () => {
    const today = dateWithZeroTime(new Date())!;
    const monthOptions = useMemo(() => getMonthOptions(today), [today]);
    const defaultMonthOption = monthOptions[1] ?? monthOptions[0];

    const [working, setWorking] = useState(false);
    const [periodFrom, setPeriodFrom] = useState<MaybeDate>();
    const [periodTo, setPeriodTo] = useState<MaybeDate>();
    const [selectedMonth, setSelectedMonth] = useState<string>(defaultMonthOption.value);
    const [dpPeriodFrom, setDpPeriodFrom] = useState<MaybeDate>(defaultMonthOption.periodFrom);
    const [dpPeriodTo, setDpPeriodTo] = useState<MaybeDate>(defaultMonthOption.periodTo);
    const [holidayDates, setHolidayDates] = useState<string[] | undefined>();
    const [report, setReport] = useState<RRReportType[] | undefined>(undefined);
    const currentEmployee = useAppSelector(selectCurrentEmployee);
    const canSeePayments = currentEmployee?.isSuperAdmin || (currentEmployee?.isAdmin && currentEmployee?.canSeePayment);
    const tblRef = useRef<HTMLTableElement>(null);

    const employeesQuery = useMemo(() => query(
        collection(appFirestore, "employees"),
        where("isLogistics", "==", true),
        where("isEmployee", "==", true)
    ).withConverter(employeesFBConverter), []);
    const [employees, , errorEmployees] = useCollectionData(employeesQuery);

    useEffect(() => {
        setHolidayDates(undefined);

        if (periodFrom && periodTo) {
            getDocs(query(collection(appFirestore, "bankHolidays"),
                where("dateObject", ">=", periodFrom),
                where("dateObject", "<=", periodTo),
                where("department", "==", stringAsDepartment("logistics"))
            )).then(
                holidays => {
                    setHolidayDates(holidays.docs.map(d => d.data().date))
                }
            );
        }
    }, [periodFrom, periodTo]);


    const calculateReport = React.useCallback(async () => {
        if (!periodFrom || !periodTo || !employees) {
            setReport([]);
            return;
        }

        const logisticsCollection = collection(appFirestore, "logistics").withConverter(logisticsFBConverter);
        const getLogisticsConstraints = (dateField: "startDate" | "reportStartDate"): QueryConstraint[] => [
            where(dateField, ">=", periodFrom),
            where(dateField, "<=", periodTo),
        ];
        const [plannedStartSnapshot, reportedStartSnapshot] = await Promise.all([
            getDocs(query(logisticsCollection, ...getLogisticsConstraints("startDate"))),
            getDocs(query(logisticsCollection, ...getLogisticsConstraints("reportStartDate"))),
        ]);
        const logistics = mergeLogisticsById(
            plannedStartSnapshot.docs.map(document => document.data()),
            reportedStartSnapshot.docs.map(document => document.data())
        );

        const report: RRReportType[] = [];
        employees?.forEach(empl => {
            const driverLogistics = logistics.filter(l => l.driverId === empl.id);

            driverLogistics.forEach(l => {
                    const resolvedPeriod = resolveLogisticPeriod(l);
                    if (!resolvedPeriod || !isResolvedPeriodStartInRange(resolvedPeriod, periodFrom, periodTo)) {
                        return;
                    }

                    const reportStartDate = resolvedPeriod.reportStartDate;
                    const reportEndDate = resolvedPeriod.reportEndDate;

                    const existingItem = report.find(r =>
                        r.driverId === empl.id &&
                        dateToString(r.date) === dateToString(reportStartDate)
                    );

                    const isBankHoliday = !!holidayDates?.includes(dateToString(reportStartDate));
                    const isEmployeeHoliday = (l.dayType === "HOLIDAY");
                    const {regularHours, overtimeHours} = calculateLogisticsReportHours({
                        reportStartDate,
                        reportEndDate,
                        isBankHoliday,
                        isEmployeeHoliday,
                    });


                    if (existingItem) {
                        existingItem.courses.push(l);
                        existingItem.distanceKm += (l.endKm && l.startKm) ? (l.endKm - l.startKm) : 0;
                        existingItem.regularHours += regularHours;
                        existingItem.overtimeHours += overtimeHours;
                        existingItem.regPrice = existingItem.regularHours * existingItem.regRate;
                        existingItem.overPrice = existingItem.overtimeHours * existingItem.overRate;

                        // ensure flags reflect computed values
                        existingItem.isBankHoliday = !!existingItem.isBankHoliday || isBankHoliday;
                        existingItem.isEmployeeHoliday = !!existingItem.isEmployeeHoliday || isEmployeeHoliday;
                    } else {
                        report.push({
                            date: reportStartDate,
                            driverId: empl.id!,
                            driverName: empl.name,
                            regularHours: regularHours,
                            overtimeHours: overtimeHours,
                            distanceKm: (l.endKm && l.startKm) ? (l.endKm - l.startKm) : 0,
                            courses: [l],
                            regRate: empl.pph || 0,
                            overRate: empl.supervisor_pph || 0,
                            regPrice: (empl.pph || 0) * regularHours,
                            overPrice: (empl.supervisor_pph || 0) * overtimeHours,
                            minHour: reportStartDate.getHours(),
                            maxHour: reportEndDate.getHours(),
                            isEmployeeHoliday: isEmployeeHoliday,
                            isBankHoliday: isBankHoliday,
                        });
                    }
                });
        });
        setReport(report);
    }, [periodFrom, periodTo, employees, holidayDates]);

    useEffect(() => {
        setReport(undefined);
        if (periodFrom && periodTo && holidayDates && employees) {
            try {
                setWorking(true);
                void calculateReport();
            } catch (e: any) {
                showErrorsListInToast("Възникна грешка", [e.message]);
                setReport(undefined);
            } finally {
                setWorking(false)
            }
        }
    }, [periodFrom, periodTo, employees, holidayDates, calculateReport])

    function setRefPeriod() {
        setPeriodFrom(dateWithZeroTime(dpPeriodFrom));
        setPeriodTo(dateWithMaxTime(dpPeriodTo));
    }

    const dates = useMemo(() => {
        if (periodFrom && periodTo) {
            return getDatesInPeriod(periodFrom, periodTo).map(d => dateToString(d));
        } else return [];
    }, [periodFrom, periodTo]);

    // Изборът на месец синхронизира работните дати във филтъра без да променя логиката на справката.
    function applyMonthSelection(optionValue: string) {
        const selectedOption = monthOptions.find(option => option.value === optionValue);
        if (!selectedOption) {
            return;
        }

        setSelectedMonth(selectedOption.value);
        setDpPeriodFrom(selectedOption.periodFrom);
        setDpPeriodTo(selectedOption.periodTo);
    }

    function exportExcel(): void {
        const workbook = utils.table_to_book(tblRef.current,);
        writeFile(workbook, "Report.xlsx");
    }

    return <Container fluid>
        <h1>Справки и отчети</h1>
        <h3>Справка за км и часове</h3>

        <Row className={"g-2 mt-3 flex-nowrap"}>
            <Col>
                <Row className={"lh-2-5em flex-nowrap"}>
                    <Col xs={"auto"}>
                        <Row>
                            <Col xs={"auto"} className={"lh-2-5em"}>
                                Месец:
                            </Col>
                            <Col xs={"auto"} className={"d-inline-block"}>
                                <FormSelect
                                    className={"mb-2"}
                                    disabled={working}
                                    value={selectedMonth}
                                    onChange={event => applyMonthSelection(event.target.value)}
                                >
                                    {
                                        monthOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))
                                    }
                                </FormSelect>
                            </Col>
                        </Row>
                    </Col>
                    <Col xs={"auto"}>
                        <Row>
                            <Col xs={"auto"} className={"lh-2-5em"}>
                                Начална дата:
                            </Col>
                            <Col xs={"auto"} className={"d-inline-block"}>
                                <InputGroup className="mb-2 w-180px flex-nowrap">
                                    <ReactDatePicker
                                        disabled={working}
                                        id={"periodFromDate"}
                                        className={"form-control flex-nowrap"}
                                        selected={dpPeriodFrom}
                                        onChange={date => setDpPeriodFrom(date || defaultMonthOption.periodFrom)}
                                        value={dateToString(dpPeriodFrom)}
                                        maxDate={dpPeriodTo}
                                    />
                                    <label htmlFor={"periodFromDate"}>
                                        <InputGroup.Text className={"lh-2-5em"}>
                                            <FontAwesomeIcon icon={faCalendar} size={"xl"}/>
                                        </InputGroup.Text>
                                    </label>
                                </InputGroup>
                            </Col>
                        </Row>
                    </Col>
                    <Col xs={"auto"}>
                        <Row>
                            <Col xs={"auto"} className={"lh-2-5em"}>
                                Крайна дата:
                            </Col>
                            <Col xs={"auto"} className={"d-inline-block"}>
                                <InputGroup className="mb-2 w-180px flex-nowrap">
                                    <ReactDatePicker
                                        disabled={working}
                                        id={"periodToDate"}
                                        className={"form-control flex-nowrap"}
                                        selected={dpPeriodTo}
                                        onChange={date => setDpPeriodTo(date || today)}
                                        value={dateToString(dpPeriodTo)}
                                        minDate={dpPeriodFrom}
                                    />
                                    <label htmlFor={"periodToDate"}>
                                        <InputGroup.Text className={"lh-2-5em"}>
                                            <FontAwesomeIcon icon={faCalendar} size={"xl"}/>
                                        </InputGroup.Text>
                                    </label>
                                </InputGroup>
                            </Col>
                        </Row>
                    </Col>
                    <Col>
                        {
                            !working && <>
                                <Button onClick={setRefPeriod}>Изчисли</Button>&nbsp;
                                <Button onClick={() => exportExcel()} variant={"outline-success"}><FontAwesomeIcon
                                    icon={faFileExcel}/></Button>&nbsp;
                            </>
                        }
                    </Col>
                </Row>
            </Col>
        </Row>

        {
            // Show report loading
            working &&
            <Row className={"g-2 mt-3"}>
                <Col xs={"12"}>
                    <Badge bg={"info"} className={"p-2"}>Изготвяне на справката, моля изчакайте...</Badge>
                </Col>
            </Row>
        }

        {
            // Show report loading error
            errorEmployees &&
            <Row className={"g-2 mt-3"}>
                <Col xs={"12"}>
                    <Badge bg={"danger"} className={"p-2"}>Възникна грешка при зареждане на
                        служителите: {errorEmployees.message}</Badge>
                </Col>
            </Row>
        }

        {
            // Show no data found
            !working && !errorEmployees && report && report.length === 0 &&
            <Row className={"g-2 mt-3"}>
                <Col xs={"12"}>
                    <Badge bg={"warning"} className={"p-2"}>Няма намерени данни за посочения период...</Badge>
                </Col>
            </Row>
        }

        {
            // Show report data
            // Table's columns are:
            // Employee | date1 min hour | date1 max hour | date 1 total hours | date2 min hour | date2 max hour | date 2 total hours | ... | Total hours | Total regular hours | Total overtime hours | Total regular price | Total overtime price

            !working && !errorEmployees && report && report.length > 0 &&
            <Row className={"g-2 mt-3 bg-white"}>
                <Col xs={"12"}>
                    <table ref={tblRef} className={"table table-sm table-bordered"}>
                        <thead>
                        <tr style={{backgroundColor: "antiquewhite"}}>
                            <th rowSpan={2}>Служител</th>
                            {dates.map(date => (
                                <th key={date} colSpan={3} className={"text-center"}>
                                    <div>
                                        <span>{date}</span>
                                        {holidayDates && holidayDates.includes(date) && (
                                            <span className={"ms-1 text-danger fw-bold"}>H</span>
                                        )}
                                    </div>
                                </th>
                            ))}
                            <th colSpan={4} className={"text-center"}>Общо</th>
                        </tr>
                        <tr style={{backgroundColor: "antiquewhite"}}>
                            {dates.map(date => (
                                <>
                                    <th key={`${date}_min`} className={"text-center"}>От</th>
                                    <th key={`${date}_max`} className={"text-center"}>До</th>
                                    <th key={`${date}_total`} className={"text-center"}>Общо</th>
                                </>
                            ))}
                            <th className={"text-center"}>Общо</th>
                            <th className={"text-center"}>Редовни</th>
                            <th className={"text-center"}>Извънредни</th>
                            <th className={"text-center"}>Км</th>
                        </tr>
                        </thead>
                        <tbody>
                        {employees?.map(empl => {
                            const emplReport = report.filter(r => r.driverId === empl.id);
                            if (emplReport.length === 0) return null;

                            const totalHours = emplReport.reduce((sum, r) => sum + r.regularHours + r.overtimeHours, 0);
                            const totalRegularHours = emplReport.reduce((sum, r) => sum + r.regularHours, 0);
                            const totalOvertimeHours = emplReport.reduce((sum, r) => sum + r.overtimeHours, 0);
                            const totalDistanceKm = emplReport.reduce((sum, r) => sum + r.distanceKm, 0);

                            return (<Fragment key={empl.id}>
                                <tr><td/></tr>
                                <tr>
                                    <td
                                        style={{border: "solid 2px", minWidth: "150px"}}
                                        rowSpan={2}
                                    >{empl.name}</td>
                                    {dates.map(date => {
                                        const dayReport =
                                            emplReport.find(r => dateToString(r.date) === date);
                                        return (
                                            <Fragment key={`${empl.id}:${date}`}>
                                                {
                                                    dayReport?.isEmployeeHoliday ?
                                                        <td key={`${empl.id}_${date}_holiday`}
                                                            className={"text-center bg-warning"}
                                                            style={{
                                                                borderLeft: "solid 2px",
                                                                borderBottom: "solid 1px",
                                                                borderTop: "solid 2px"
                                                            }}
                                                            title={"Почивен ден"}
                                                            colSpan={2}
                                                        >
                                                            <b>ПОЧ</b>
                                                        </td>
                                                        :
                                                        <>
                                                            <td key={`${empl.id}_${date}_min`}
                                                                className={"text-center"}
                                                                style={{
                                                                    borderLeft: "solid 2px",
                                                                    borderBottom: "solid 1px",
                                                                    borderTop: "solid 2px"
                                                                }}
                                                            >
                                                                {dayReport ? dayReport.minHour : ""}
                                                            </td>
                                                            <td key={`${empl.id}_${date}_max`}
                                                                className={"text-center"}
                                                                style={{
                                                                    borderBottom: "solid 1px",
                                                                    borderTop: "solid 2px"
                                                                }}
                                                            >
                                                                {dayReport ? dayReport.maxHour : ""}
                                                            </td>
                                                        </>
                                                }
                                                <td key={`${empl.id}_${date}_total`}
                                                    className={"text-center bg-secondary-light"}
                                                    style={{borderBottom: "solid 1px", borderTop: "solid 2px"}}
                                                >
                                                    {dayReport ? (() => {
                                                        const regHoursStr = hoursAsShortString(dayReport.regularHours);
                                                        const overHoursStr = hoursAsShortString(dayReport.overtimeHours);
                                                        const regPrice = zeroToEmptyStringAsCurrency((dayReport.regularHours * (dayReport.regRate || 0)).toString());
                                                        const overPrice = zeroToEmptyStringAsCurrency((dayReport.overtimeHours * (dayReport.overRate || 0)).toString());
                                                        const totalHoursStr = hoursAsShortString(dayReport.regularHours + dayReport.overtimeHours);
                                                        // build title lines only for non-zero values for better readability
                                                        const titleLines: string[] = [];
                                                        if ((dayReport.regularHours || 0) > 0) {
                                                            titleLines.push(`Редовни: ${regHoursStr} @ ${dayReport.regRate || 0} €/ч = ${regPrice}`);
                                                        }
                                                        if ((dayReport.overtimeHours || 0) > 0) {
                                                            titleLines.push(`Извънредни: ${overHoursStr} @ ${dayReport.overRate || 0} €/ч = ${overPrice}`);
                                                        }
                                                        // always include total for clarity
                                                        titleLines.push(`Общо: ${totalHoursStr}`);
                                                        (dayReport as any).__title = titleLines.join('\n');
                                                         return <b>{totalHoursStr}</b>;
                                                    })() : ""}
                                                </td>
                                            </Fragment>
                                        );
                                    })}
                                    <td
                                        className={"text-center bg-secondary text-white fw-bold"}
                                        style={{border: "solid 2px black"}}>
                                        {hoursAsShortString(totalHours)}
                                    </td>
                                    <td className={"text-center bg-secondary text-white fw-bold"}
                                        style={{border: "solid 2px black"}}>
                                        {hoursAsShortString(totalRegularHours)}
                                    </td>
                                    <td className={"text-center bg-secondary text-white fw-bold"}
                                        style={{border: "solid 2px black"}}>
                                        {hoursAsShortString(totalOvertimeHours)}
                                    </td>
                                    <td rowSpan={2}
                                        className={"text-center bg-secondary text-white fw-bold"}
                                        style={{border: "solid 1px black"}}>
                                        {totalDistanceKm} км
                                    </td>
                                </tr>
                                {canSeePayments && (
                                    <tr>
                                        {dates.map(date => {
                                            const dayReport =
                                                emplReport.find(r => dateToString(r.date) === date);
                                            return (<Fragment key={`${empl.id}:prices:${date}`}>
                                                <td colSpan={2}
                                                    style={{
                                                        borderLeft: "solid 2px",
                                                        borderBottom: "solid 2px"
                                                    }}
                                                >
                                                    {
                                                        (dayReport && dayReport.distanceKm) ?
                                                            `${dayReport.distanceKm} км` : ""
                                                    }
                                                </td>
                                                <td
                                                    className={"text-end fw-bold"}
                                                    title={dayReport ? (dayReport as any).__title : undefined}
                                                    style={{
                                                        borderBottom: "solid 2px"
                                                    }}
                                                >
                                                    {zeroToEmptyStringAsCurrency(
                                                        (dayReport ? (dayReport.regPrice + dayReport.overPrice) : 0).toString()
                                                    )}
                                                </td>
                                            </Fragment>);
                                        })}
                                        <td
                                            className={"text-center bg-secondary text-white fw-bold"}
                                            style={{border: "solid 1px black"}}>
                                            {zeroToEmptyStringAsCurrency(
                                                (totalRegularHours * (empl.pph || 0) +
                                                    totalOvertimeHours * (empl.supervisor_pph || 0)).toString()
                                            )}
                                        </td>
                                        <td
                                            className={"text-center bg-secondary text-white fw-bold"}
                                            style={{border: "solid 1px black"}}>
                                            {zeroToEmptyStringAsCurrency(
                                                (totalRegularHours * (empl.pph || 0)).toString()
                                            )}
                                        </td>
                                        <td
                                            className={"text-center bg-secondary text-white fw-bold"}
                                            style={{border: "solid 1px black"}}>
                                            {zeroToEmptyStringAsCurrency(
                                                (totalOvertimeHours * (empl.supervisor_pph || 0)).toString()
                                            )}
                                        </td>
                                    </tr>
                                )}
                            </Fragment>)
                        })}
                        </tbody>
                    </table>
                </Col>
            </Row>
        }
    </Container>
}

export default LogisticsTimeReportView;
